// Écran blanc → rapport serveur. Un plantage React (ErrorBoundary), un morceau de l'app introuvable après un
// déploiement (chunk) ou une erreur de programmation non rattrapée est envoyé à POST /api/client-crashes
// (message, pile, page, version, appareil) → Admin › Supervision › Historique des incidents (kind « crash »).
import { API_URL, getToken, getDeviceId } from "@/lib/api";
import { appPlatform } from "@/lib/platform";
import { APP_VERSION, WEB_BUILD } from "@/lib/appVersion";

const CHUNK_RX = /loading (css )?chunk|chunkloaderror|failed to fetch dynamically imported module|importing a module script failed|unexpected token '<'|invalid or unexpected token/i;
const RELOAD_KEY = "yalola.chunkReloadedAt";
// Paramètres de suivi (Facebook fbclid, Google gclid, utm_*…) : retirés de la page rapportée.
const TRACKING_RX = /^(fbclid|gclid|igshid|msclkid|_ga|mc_cid|mc_eid|utm_\w+)$/i;
const sent = new Set();
let reloading = false;

// Lot 671 — rechargement VOULU par l'app (chunk manquant, « Mettre à jour », « Réessayer ») : noté avant
// location.reload() pour que l'enregistreur de vol (lib/flightRecorder.js) ne le compte pas comme un redémarrage inattendu.
const INTENT_KEY = "yalola.flight.intent";
export const markIntentionalReload = (reason = "reload") => {
  try { localStorage.setItem(INTENT_KEY, JSON.stringify({ reason, at: Date.now() })); } catch { /* noop */ }
};
export const intentionalReloadPending = () => {
  try {
    const raw = localStorage.getItem(INTENT_KEY);
    localStorage.removeItem(INTENT_KEY);
    const v = raw ? JSON.parse(raw) : null;
    return !!v && Date.now() - (v.at || 0) < 60 * 1000;
  } catch { return false; }
};

export const isChunkLoadError = (err) => {
  if (!err) return false;
  return err.name === "ChunkLoadError" || CHUNK_RX.test(String(err.message || err));
};

export const crashPath = () => {
  const { pathname, search } = window.location;
  let qs = String(search || "").replace(/^\?/, "");
  try {
    const p = new URLSearchParams(search);
    [...p.keys()].filter((k) => TRACKING_RX.test(k)).forEach((k) => p.delete(k));
    qs = p.toString();
  } catch { /* noop */ }
  return `${pathname}${qs ? `?${qs}` : ""}`.slice(0, 160);
};

// Rechargement AUTOMATIQUE une seule fois par 5 min : un vieux bundle qui demande un morceau disparu après un
// déploiement, ou un fichier JS reçu tronqué (réseau, navigateur intégré Facebook…), se répare tout seul
// (au lieu d'un écran blanc) ; une 2e fois → écran de secours classique.
export const reloadOnceForChunk = () => {
  let last = 0;
  try { last = Number(sessionStorage.getItem(RELOAD_KEY) || 0); } catch { /* noop */ }
  if (Date.now() - last < 5 * 60 * 1000) return false;
  try { sessionStorage.setItem(RELOAD_KEY, String(Date.now())); } catch { /* noop */ }
  reloading = true;
  markIntentionalReload("chunk");
  window.location.reload();
  return true;
};

export const reportClientCrash = async ({ error, componentStack = "", kind = "render", foreground } = {}) => {
  if (reloading) return false;
  const message = String(error?.message || error || "Erreur inconnue").slice(0, 500);
  const key = `${kind}:${message}`;
  if (sent.has(key)) return false;
  sent.add(key);
  const body = {
    kind: isChunkLoadError(error) ? "chunk" : kind,
    message,
    stack: String(error?.stack || "").slice(0, 16000), // lot 716 — trace ANR Android tous fils (le serveur garde 16 000)
    component_stack: String(componentStack || "").slice(0, 2000),
    path: crashPath(),
    platform: appPlatform(),
    app_version: APP_VERSION,
    web_build: WEB_BUILD,
  };
  // Lot 691 — sorties système : l'app était-elle au premier plan ? (alerte admin immédiate côté serveur)
  if (typeof foreground === "boolean") body.foreground = foreground;
  try {
    const headers = { "Content-Type": "application/json", "x-device-id": getDeviceId() };
    const t = getToken();
    if (t) headers.Authorization = `Bearer ${t}`;
    await fetch(`${API_URL}/client-crashes`, { method: "POST", headers, body: JSON.stringify(body), keepalive: true });
    return true;
  } catch {
    return false;
  }
};

// Erreurs de programmation hors rendu React (gestionnaires d'événements, promesses) : seules les vraies
// exceptions JS sont remontées — jamais les erreurs réseau (axios) ni les rejets sans pile.
const PROGRAMMING_ERRORS = ["TypeError", "ReferenceError", "RangeError", "SyntaxError"];
const isProgrammingError = (e) => !!e && typeof e === "object" && PROGRAMMING_ERRORS.includes(e.name) && !e.isAxiosError && !e.friendlyMessage;

const onChunkError = (err) => { if (!reloadOnceForChunk()) reportClientCrash({ error: err, kind: "chunk" }); };

export const installGlobalCrashHandlers = () => {
  const onError = (ev) => {
    // Un script de l'app tronqué/corrompu arrive ici en SyntaxError (« Invalid or unexpected token ») : on recharge.
    if (isChunkLoadError(ev?.error || ev?.message)) { onChunkError(ev?.error || new Error(String(ev?.message))); return; }
    if (ev?.error && isProgrammingError(ev.error)) reportClientCrash({ error: ev.error, kind: "unhandled" });
  };
  const onRejection = (ev) => {
    const r = ev?.reason;
    if (isChunkLoadError(r)) { onChunkError(r); return; }
    if (isProgrammingError(r)) reportClientCrash({ error: r, kind: "unhandled" });
  };
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onRejection);
  return () => { window.removeEventListener("error", onError); window.removeEventListener("unhandledrejection", onRejection); };
};
